/**
 * PATH: packages/ui/src/stores/createTabStore.ts
 * PURPOSE: Factory for creating per-hub Zustand tab stores.
 *
 * WHY: Every hub needs browser-style tab state (open/close/pin/reorder)
 *      with localStorage persistence. This factory parameterizes the
 *      CRM-proven pattern so any hub can create a tab store in one call.
 *
 * USAGE:
 *   const useWorkforceTabStore = createTabStore({
 *     hubId: 'workforce',
 *     defaultTab: { path: '/people', title: 'People', iconKey: 'Users' },
 *   })
 *
 * DEPENDENCIES:
 *  - zustand: State management
 *  - zustand/middleware: persist for localStorage
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

export interface Tab {
  id: string
  path: string
  title: string
  /** Icon key (stored as string for serialization; resolved at render time) */
  iconKey?: string
  isPinned: boolean
  isDirty?: boolean
  /**
   * Marks the tab as a draft (in-progress, server-backed `dr-…` quote that
   * has not been promoted to a real Quote yet). Renders with a light yellow
   * tint in the TabBar so estimators can spot drafts at a glance.
   */
  isDraft?: boolean
  /** Remembered scroll position */
  scrollY?: number
}

export interface TabState {
  tabs: Tab[]
  activeTabId: string | null
  /** Maximum number of open tabs before auto-closing oldest */
  maxTabs: number
}

export interface TabActions {
  /** Open a new tab or activate existing one for the same path */
  openTab: (path: string, title: string, iconKey?: string) => void
  /** Close a tab by ID */
  closeTab: (tabId: string) => void
  /** Set the active tab */
  setActiveTab: (tabId: string) => void
  /** Toggle pin state of a tab */
  togglePin: (tabId: string) => void
  /** Close all tabs except the given one (and pinned tabs) */
  closeOthers: (tabId: string) => void
  /** Duplicate a tab */
  duplicateTab: (tabId: string) => void
  /** Update the path/title of the active tab (for in-tab navigation) */
  updateActiveTab: (path: string, title?: string, iconKey?: string) => void
  /** Save scroll position for a tab */
  saveScrollPosition: (tabId: string, scrollY: number) => void
  /** Mark a tab as dirty (unsaved changes) */
  setDirty: (tabId: string, isDirty: boolean) => void
  /**
   * Update the title (and optionally iconKey) of a specific tab — used by
   * pages that resolve a friendlier label after async data loads (e.g.
   * "Quote 982" → "Q-1391-01 · Acme Steel"). Pages that want to update
   * the *currently active* tab can also use `updateActiveTab`, but this
   * keyed variant is safer when the user has already navigated elsewhere
   * by the time the data resolves.
   */
  setTabTitle: (tabId: string, title: string, iconKey?: string) => void
  /**
   * Toggle the `isDraft` flag on a specific tab. Used by the Quote
   * Builder when a `dr-…` draft is promoted to a real quote (yellow
   * tint clears, regular chrome takes over).
   */
  setDraft: (tabId: string, isDraft: boolean) => void
}

export type TabStore = TabState & TabActions

// ─────────────────────────────────────────────────────────────
// Config
// ─────────────────────────────────────────────────────────────

export interface TabStoreConfig {
  /** Hub identifier — used for localStorage key (e.g. 'workforce' → 'quotech-workforce-tabs') */
  hubId: string
  /** The default "home" tab that's always present and pinned */
  defaultTab: {
    path: string
    title: string
    iconKey: string
  }
  /** Maximum number of open tabs before auto-closing oldest (default: 15) */
  maxTabs?: number
}

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

/** Generate a unique tab ID */
function generateTabId(): string {
  return `tab-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
}

/**
 * Find an existing tab that matches a given path (exact match only).
 */
function findTabByPath(tabs: Tab[], path: string): Tab | undefined {
  return tabs.find(t => t.path === path)
}

// ─────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────

/**
 * Create a Zustand tab store for a specific hub.
 *
 * @example
 * ```ts
 * const useWorkforceTabStore = createTabStore({
 *   hubId: 'workforce',
 *   defaultTab: { path: '/people', title: 'People', iconKey: 'Users' },
 * })
 * ```
 */
export function createTabStore(config: TabStoreConfig) {
  const { hubId, defaultTab: defaultTabConfig, maxTabs: configMaxTabs = 15 } = config

  const DEFAULT_TAB: Tab = {
    id: `tab-${hubId}-home`,
    path: defaultTabConfig.path,
    title: defaultTabConfig.title,
    iconKey: defaultTabConfig.iconKey,
    isPinned: true,
    isDirty: false,
  }

  return create<TabStore>()(
    persist(
      (set, get) => ({
        // Initial state
        tabs: [DEFAULT_TAB],
        activeTabId: DEFAULT_TAB.id,
        maxTabs: configMaxTabs,

        openTab: (path, title, iconKey) => {
          const { tabs, maxTabs } = get()

          // Check if a tab with this exact path already exists
          const existing = findTabByPath(tabs, path)
          if (existing) {
            set({ activeTabId: existing.id })
            return
          }

          // Create new tab
          const newTab: Tab = {
            id: generateTabId(),
            path,
            title,
            iconKey,
            isPinned: false,
            isDirty: false,
          }

          let updatedTabs = [...tabs, newTab]

          // If we exceed max tabs, close the oldest unpinned non-active tab
          const unpinned = updatedTabs.filter(t => !t.isPinned)
          if (unpinned.length > maxTabs) {
            const toRemove = unpinned.find(t => t.id !== newTab.id && t.id !== get().activeTabId)
            if (toRemove) {
              updatedTabs = updatedTabs.filter(t => t.id !== toRemove.id)
            }
          }

          set({ tabs: updatedTabs, activeTabId: newTab.id })
        },

        closeTab: (tabId) => {
          const { tabs, activeTabId } = get()
          const tab = tabs.find(t => t.id === tabId)

          // Can't close pinned tabs or the last tab
          if (!tab || tab.isPinned) return
          if (tabs.length <= 1) return

          const tabIndex = tabs.findIndex(t => t.id === tabId)
          const updatedTabs = tabs.filter(t => t.id !== tabId)

          // If closing the active tab, switch to adjacent tab
          let newActiveId = activeTabId
          if (activeTabId === tabId) {
            const nextTab = tabs[tabIndex + 1] || tabs[tabIndex - 1] || updatedTabs[0]
            newActiveId = nextTab?.id ?? null
          }

          set({ tabs: updatedTabs, activeTabId: newActiveId })
        },

        setActiveTab: (tabId) => {
          const tab = get().tabs.find(t => t.id === tabId)
          if (tab) {
            set({ activeTabId: tabId })
          }
        },

        togglePin: (tabId) => {
          set((state) => ({
            tabs: state.tabs.map(t =>
              t.id === tabId ? { ...t, isPinned: !t.isPinned } : t
            ),
          }))
        },

        closeOthers: (tabId) => {
          set((state) => ({
            tabs: state.tabs.filter(t => t.id === tabId || t.isPinned),
            activeTabId: tabId,
          }))
        },

        duplicateTab: (tabId) => {
          const { tabs } = get()
          const source = tabs.find(t => t.id === tabId)
          if (!source) return

          const newTab: Tab = {
            id: generateTabId(),
            path: source.path,
            title: source.title,
            iconKey: source.iconKey,
            isPinned: false,
            isDirty: false,
          }

          set((state) => ({
            tabs: [...state.tabs, newTab],
            activeTabId: newTab.id,
          }))
        },

        updateActiveTab: (path, title, iconKey) => {
          const { activeTabId } = get()
          if (!activeTabId) return

          set((state) => ({
            tabs: state.tabs.map(t =>
              t.id === activeTabId
                ? {
                    ...t,
                    path,
                    ...(title !== undefined && { title }),
                    ...(iconKey !== undefined && { iconKey }),
                  }
                : t
            ),
          }))
        },

        saveScrollPosition: (tabId, scrollY) => {
          set((state) => ({
            tabs: state.tabs.map(t =>
              t.id === tabId ? { ...t, scrollY } : t
            ),
          }))
        },

        setDirty: (tabId, isDirty) => {
          set((state) => ({
            tabs: state.tabs.map(t =>
              t.id === tabId ? { ...t, isDirty } : t
            ),
          }))
        },

        setTabTitle: (tabId, title, iconKey) => {
          set((state) => ({
            tabs: state.tabs.map(t =>
              t.id === tabId
                ? {
                    ...t,
                    title,
                    ...(iconKey !== undefined && { iconKey }),
                  }
                : t
            ),
          }))
        },

        setDraft: (tabId, isDraft) => {
          set((state) => ({
            tabs: state.tabs.map(t =>
              t.id === tabId ? { ...t, isDraft } : t
            ),
          }))
        },
      }),
      {
        name: `quotech-${hubId}-tabs`,
        // Only persist serializable fields (not scroll positions)
        partialize: (state) => ({
          tabs: state.tabs.map(({ scrollY, ...rest }) => rest),
          activeTabId: state.activeTabId,
          maxTabs: state.maxTabs,
        }),
        // CRITICAL: Validate hydrated state so tabs is NEVER empty.
        //
        // 2026-05-15 — also DEDUPE by path. An older bundle wrote
        // duplicate entries into the persisted store (operator
        // screenshot showed 4 ``Board`` and 2 ``Email`` tabs at
        // once, which the current ``openTab`` could never produce
        // because ``findTabByPath`` returns the existing tab and
        // just re-activates it). Without dedup at hydrate, those
        // duplicates persist forever and confuse every downstream
        // consumer (``findTabByPath`` keeps picking the FIRST
        // entry by ``path`` so subsequent activates point at a
        // stale id, the TabBar renders the duplicates verbatim,
        // and ``activeTabId`` can land on a duplicate that's not
        // the one the user clicked). Dedup is path-keyed because
        // ``path`` is the user-visible identity of a tab — two
        // tabs with the same path are by definition the same
        // logical tab.
        merge: (persistedState, currentState) => {
          const persisted = (persistedState ?? {}) as Partial<TabState>
          const merged = { ...currentState, ...persisted }

          if (!Array.isArray(merged.tabs) || merged.tabs.length === 0) {
            merged.tabs = [DEFAULT_TAB]
            merged.activeTabId = DEFAULT_TAB.id
          } else {
            // Dedupe by path. Keep the FIRST occurrence (which is
            // typically the pinned default at slot 0 — preserves
            // pin state and the canonical id). Forward the active
            // selection: if activeTabId pointed at a duplicate
            // that we drop, remap to the surviving entry's id so
            // the user's selection survives the migration.
            const seen = new Set<string>()
            const idRemap = new Map<string, string>()
            const deduped: Tab[] = []
            for (const tab of merged.tabs) {
              if (!tab || typeof tab.path !== 'string') continue
              if (seen.has(tab.path)) {
                const survivor = deduped.find(t => t.path === tab.path)
                if (survivor) idRemap.set(tab.id, survivor.id)
                continue
              }
              seen.add(tab.path)
              deduped.push(tab)
            }
            merged.tabs = deduped
            if (merged.activeTabId && idRemap.has(merged.activeTabId)) {
              merged.activeTabId = idRemap.get(merged.activeTabId)!
            }
          }

          // Ensure activeTabId points to a valid tab AFTER dedup.
          const hasActive = merged.tabs.some(t => t.id === merged.activeTabId)
          if (!hasActive) {
            merged.activeTabId = merged.tabs[0]?.id ?? DEFAULT_TAB.id
          }

          return merged as TabStore
        },
      }
    )
  )
}

export default createTabStore
